import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import styles from './DirectoryPage.module.css';

interface Member {
  id: string;
  name: string;
  company?: string;
  role?: string;
  bio?: string;
  avatar_url?: string;
  linkedin_url?: string;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

const DirectoryPage: React.FC = () => {
  const { member: authMember } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProfile, setSelectedProfile] = useState<Member | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const { data, error } = await supabase
          .from('b2b_members')
          .select('id, name, company, role, bio, avatar_url, linkedin_url')
          .order('name');

        if (error) throw error;
        setMembers(data || []);
      } catch {
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, []);

  const filtered = members.filter((m) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      m.name.toLowerCase().includes(q) ||
      (m.company && m.company.toLowerCase().includes(q)) ||
      (m.role && m.role.toLowerCase().includes(q))
    );
  });

  const resizeImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = 300;
          canvas.height = 300;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas context unavailable'));

          // Center-crop: take the largest square from center
          const size = Math.min(img.width, img.height);
          const sx = (img.width - size) / 2;
          const sy = (img.height - size) / 2;
          ctx.drawImage(img, sx, sy, size, size, 0, 0, 300, 300);

          const base64 = canvas.toDataURL(file.type || 'image/jpeg');
          resolve(base64);
        };
        img.onerror = () => reject(new Error('Image load failed'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('File read failed'));
      reader.readAsDataURL(file);
    });
  };

  const handleAvatarUpload = async (file: File) => {
    if (!selectedProfile) return;
    setUploading(true);
    setUploadError('');

    try {
      const avatar_base64 = await resizeImage(file);
      const res = await fetch('/.netlify/functions/admin-upload-avatar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          member_id: selectedProfile.id,
          avatar_base64,
          content_type: file.type || 'image/jpeg',
        }),
      });

      if (!res.ok) throw new Error('Upload failed');

      const data = await res.json();
      const newUrl = data.avatar_url || avatar_base64;

      // Update local state
      setMembers((prev) =>
        prev.map((m) =>
          m.id === selectedProfile.id ? { ...m, avatar_url: newUrl } : m
        )
      );
      setSelectedProfile((prev) =>
        prev ? { ...prev, avatar_url: newUrl } : prev
      );
    } catch {
      setUploadError('Failed to upload avatar.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.title}>The Directory</h1>

        <div className={styles.searchWrap}>
          <input
            type="text"
            placeholder="Search a member..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>

        {loading ? (
          <p className={styles.loadingText}>Loading...</p>
        ) : filtered.length === 0 ? (
          <p className={styles.loadingText}>No members found.</p>
        ) : (
          <div className={styles.grid}>
            {filtered.map((m) => (
              <button
                key={m.id}
                className={styles.card}
                onClick={() => {
                  setSelectedProfile(m);
                  setUploadError('');
                }}
                type="button"
              >
                {m.avatar_url ? (
                  <img
                    src={m.avatar_url}
                    alt={m.name}
                    className={styles.avatar}
                  />
                ) : (
                  <div className={styles.initials}>
                    {getInitials(m.name)}
                  </div>
                )}
                <span className={styles.name}>{m.name}</span>
                {m.company && (
                  <span className={styles.company}>{m.company}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Profile Modal */}
      {selectedProfile && (
        <div
          className={styles.overlay}
          onClick={() => setSelectedProfile(null)}
          role="presentation"
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            {selectedProfile.avatar_url ? (
              <img
                src={selectedProfile.avatar_url}
                alt={selectedProfile.name}
                className={styles.modalAvatar}
              />
            ) : (
              <div className={styles.modalInitials}>
                {getInitials(selectedProfile.name)}
              </div>
            )}
            <h3 className={styles.modalName}>{selectedProfile.name}</h3>
            {selectedProfile.company && (
              <p className={styles.modalCompany}>{selectedProfile.company}</p>
            )}
            {selectedProfile.role && (
              <p className={styles.modalRole}>{selectedProfile.role}</p>
            )}
            {selectedProfile.bio && (
              <p className={styles.modalBio}>{selectedProfile.bio}</p>
            )}

            {/* Admin: change avatar */}
            {authMember?.is_admin && (
              <div className={styles.adminSection}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className={styles.hiddenInput}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleAvatarUpload(file);
                  }}
                />
                <button
                  className={styles.changePhotoBtn}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  type="button"
                >
                  {uploading ? 'Uploading...' : 'Change photo'}
                </button>
                {uploadError && (
                  <p className={styles.uploadError}>{uploadError}</p>
                )}
              </div>
            )}

            <div className={styles.modalActions}>
              {selectedProfile.linkedin_url && (
                <a
                  href={selectedProfile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.modalViewProfile}
                >
                  View Profile
                </a>
              )}
              <button
                className={styles.modalClose}
                onClick={() => setSelectedProfile(null)}
                type="button"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DirectoryPage;
